import { useState, useEffect } from 'react';
import { computeZmanim, computeZmanimDates, zmanimLabels, type ZmanimDay } from '@/lib/hebcal';
import { fetchUserProfile, profileGeo } from '@/services/profile';

const orderedKeys = [
  'alotHashachar', 'netz', 'sofZmanShemaMga', 'sofZmanShemaGra',
  'sofZmanTefilla', 'chatzot', 'minchaGedola', 'minchaKetana',
  'plagHaMincha', 'shkia', 'tzet',
];

export default function ZmanimWidget() {
  const [zmanim, setZmanim] = useState<ZmanimDay | null>(null);
  const [zmanDates, setZmanDates] = useState<Record<string, Date> | null>(null);
  const [locationLabel, setLocationLabel] = useState('');
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let mounted = true;
    fetchUserProfile().then((profile) => {
      if (!mounted) return;
      const geo = profileGeo(profile);
      const d = new Date();
      setZmanim(computeZmanim(d, geo));
      setZmanDates(computeZmanimDates(d, geo));
      setLocationLabel(geo.timezone.split('/').pop()?.replace(/_/g, ' ') || 'Local');
    });
    const interval = setInterval(() => setNow(new Date()), 30000);
    return () => { mounted = false; clearInterval(interval); };
  }, []);

  if (!zmanim || !zmanDates) {
    return (
      <div className="bg-background-100 rounded-lg border border-background-200/60 overflow-hidden animate-pulse">
        <div className="px-4 py-3 border-b border-background-200/60">
          <div className="h-5 w-24 rounded bg-background-200" />
        </div>
        <div className="p-4"><div className="h-64 rounded bg-background-200/50" /></div>
      </div>
    );
  }

  const isTimePassed = (key: string): boolean => {
    const d = zmanDates[key];
    return !!d && d.getTime() <= now.getTime();
  };

  const countdown = (() => {
    const shkia = zmanDates.shkia;
    if (!shkia) return '';
    let diffMs = shkia.getTime() - now.getTime();
    if (diffMs < 0) diffMs += 24 * 3600000; // approximate until tomorrow's sunset
    const hours = Math.floor(diffMs / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    return `${hours}h ${minutes}m`;
  })();

  const filteredKeys = orderedKeys.filter((k) => k in zmanim);

  return (
    <div className="bg-background-100 rounded-lg border border-background-200/60 overflow-hidden">
      <div className="px-4 py-3 border-b border-background-200/60 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 flex items-center justify-center">
            <i className="ri-sun-line text-amber-500 text-lg" />
          </span>
          <h3 className="text-sm font-heading font-semibold text-foreground-950">Zmanim</h3>
        </div>
        <span className="text-xs text-foreground-400 font-mono">{locationLabel}</span>
      </div>

      {/* Countdown */}
      <div className="px-4 py-3 bg-accent-50 border-b border-accent-100/50">
        <div className="flex items-center justify-between">
          <span className="text-xs text-accent-700 font-medium">Sunset in</span>
          <span className="text-lg font-heading font-bold text-accent-600 font-mono">
            {countdown || '---'}
          </span>
        </div>
      </div>

      {/* Zmanim list */}
      <div className="p-3 space-y-0.5">
        {filteredKeys.map((key, idx) => {
          const value = zmanim[key as keyof ZmanimDay];
          const label = zmanimLabels[key] || key;
          const passed = isTimePassed(key);
          const isNext = !passed && filteredKeys.slice(0, idx).every((k) => isTimePassed(k));
          const isSunset = key === 'shkia';

          return (
            <div
              key={key}
              className={`flex items-center justify-between px-2 py-1.5 rounded-md transition ${
                isNext
                  ? 'bg-accent-100/70 border border-accent-200/60'
                  : isSunset
                    ? 'bg-amber-50'
                    : ''
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  passed ? 'bg-foreground-300' : isNext ? 'bg-accent-500' : 'bg-foreground-400'
                }`} />
                <span className={`text-xs truncate ${passed ? 'text-foreground-400' : 'text-foreground-700 font-medium'}`}>
                  {label}
                </span>
              </div>
              <span className={`text-xs font-mono flex-shrink-0 ml-2 ${
                passed ? 'text-foreground-300' : isNext ? 'text-accent-700 font-semibold' : 'text-foreground-600'
              }`}>
                {value}
                {isNext && <span className="text-[10px] text-accent-500 ml-1">next</span>}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}